import { useSearchParams } from 'react-router-dom'
import ButtonBack from '../../components/button/ButtonBack'
import { Button, Divider, Form, Input, Modal, Select, Space, Tabs } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { apiAddListFeedbackHistory, apiAddListMetadata, apiGetListCompanies, apiGetListFeedbackHistory, apiGetListMetadata } from '../../api'
import { useLoading } from '../../context/LoadingContext'
import { ICompany } from '../../models/company.modal'
import { COMPANY } from '../../assets/constants/route_path'
import TabConnection from './TabConnection'
import TabPackage from './TabPackage'
import TabHistory from './TabHistory'
import TabNotifyConfig from './TabNotifyConfig'
import { toastError, toastSuccess } from '../../utils/toast'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { partnerHistoryOptions } from '../../assets/constants/default_options'
import TabInfo from './TabInfo'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'

const formatCostRate = (rate?: number) => {
    if (rate == null || rate <= 0) {
        return <span style={{ color: '#999' }}>Chưa cấu hình</span>
    }
    return <span>{(rate * 100).toFixed(2)}%</span>
}

export default function CompanyDetail() {
    const [searchParams] = useSearchParams()
    const cpCode = searchParams.get('cpCode')
    const [company, setCompany] = useState<ICompany>({})
    const [info, setInfo] = useState({ phone: '', email: '', director: '', address: '' })
    const [listInfo, setListInfo] = useState<any>({})
    const [listHistory, setListHistory] = useState<any>({})
    const [formCreateHistory] = Form.useForm()
    const [isOpenModalCreateHistory, setIsOpenModalCreateHistory] = useState(false)
    const [isOpenModalUpdateInfo, setIsOpenModalUpdateInfo] = useState(false)
    const [formUpdateInfo] = Form.useForm()
    const { setIsLoading } = useLoading()
    const authState = useSelector(authSelector)
    const { account } = authState

    const fetchCompany = useCallback(async () => {
        try {
            if (!cpCode) {
                return
            }
            setIsLoading(true)
            const resp = await apiGetListCompanies({
                limit: 1,
                code: cpCode,
            })
            if (resp.data === null || resp.data.companies?.length === 0) {
                setCompany({})
                return
            }
            setCompany(resp.data.companies[0])
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, cpCode])

    const fetchInfo = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetListMetadata({ cpCode })
            setListInfo(data)
            const partnerInfo = { phone: '', email: '', director: '', address: '' }
            data.info.map((item: any) => {
                switch (item.type) {
                    case 'PHONE':
                        partnerInfo.phone = item.result
                        break
                    case 'EMAIL':
                        partnerInfo.email = item.result
                        break
                    case 'DIRECTOR':
                        partnerInfo.director = item.result
                        break
                    case 'ADDRESS':
                        partnerInfo.address = item.result
                        break
                }
                return item
            })
            setInfo(partnerInfo)
        } catch (error) {
            console.log(error)
        } finally {
            setTimeout(() => setIsLoading(false), 500)
        }
    }, [setIsLoading, cpCode])

    const fetchHistory = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetListFeedbackHistory({ cpCode })
            setListHistory(data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, cpCode])

    useEffect(() => {
        fetchCompany()
        fetchInfo()
        fetchHistory()
    }, [fetchCompany, fetchInfo, fetchHistory])

    const handleUpdateInfo = async (values: any) => {
        try {
            setIsLoading(true)
            const { phone, email, director, address } = values
            if (!phone && !email && !director && !address) {
                toastError('Vui lòng nhập thông tin cần cập nhật!')
                return
            }
            const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
            if (phone && phone !== info.phone) {
                await apiAddListMetadata({ info: [{ cpCode: cpCode, result: phone, type: 'PHONE' }] })
                await sleep(1000)
            }
            if (email && email !== info.email) {
                await apiAddListMetadata({ info: [{ cpCode: cpCode, result: email, type: 'EMAIL' }] })
                await sleep(1000)
            }
            if (director && director !== info.director) {
                await apiAddListMetadata({ info: [{ cpCode: cpCode, result: director, type: 'DIRECTOR' }] })
                await sleep(1000)
            }
            if (address && address !== info.address) {
                await apiAddListMetadata({ info: [{ cpCode: cpCode, result: address, type: 'ADDRESS' }] })
                await sleep(1000)
            }
            setIsOpenModalUpdateInfo(false)
            formUpdateInfo.resetFields()
            fetchInfo()
            toastSuccess('Cập nhật thông tin thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleCreateHistory = async (values: any) => {
        try {
            setIsLoading(true)
            await apiAddListFeedbackHistory({ feedback: [{ cpCode: values.cpCode, result: values.result, type: values.type }] })
            setIsOpenModalCreateHistory(false)
            formCreateHistory.resetFields()
            fetchHistory()
            toastSuccess('Thêm lịch sử tương tác thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <ButtonBack to={COMPANY} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <h1>Đối tác: {company.name}</h1>
                <Space>
                    {account?.canAddListFeedbackHistory && (
                        <Button onClick={() => setIsOpenModalCreateHistory(true)}>Thêm tương tác</Button>
                    )}
                    {account?.canAddListMetadata && (
                        <Button onClick={() => setIsOpenModalUpdateInfo(true)}>Cập nhật thông tin liên hệ</Button>
                    )}
                </Space>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div>
                    <p><b>Tên công ty: </b>{company?.name}</p>
                    <p><b>Mã công ty: </b>{company?.code}</p>
                    <p><b>Chi phí đấu nối: </b>{formatCostRate(company?.costRateConnection)}</p>
                    <p><b>Chi phí gia hạn: </b>{formatCostRate(company?.costRateExtension)}</p>
                </div>
                <div>
                    <p><b>Số điện thoại: </b>{info.phone}</p>
                    <p><b>Email: </b>{info.email}</p>
                </div>
                <div>
                    <p><b>Đại diện: </b>{info.director}</p>
                    <p><b>Địa chỉ: </b>{info.address}</p>
                </div>
                <div></div>
                <div></div>
            </div>
            <Divider />
            <Tabs
                size='middle'
                items={[
                    {
                        key: 'connection',
                        label: 'Đấu nối',
                        children: (
                            <TabConnection cpCode={cpCode} />
                        ),
                    },
                    {
                        key: 'package',
                        label: 'Gói cước',
                        children: (
                            <TabPackage cpCode={cpCode} />
                        ),
                    },
                    {
                        key: 'history',
                        label: 'Lịch sử tương tác',
                        children: (
                            <TabHistory history={listHistory} />
                        ),
                    },
                    {
                        key: 'info',
                        label: 'Lịch sử cập nhật thông tin liên hệ',
                        children: (
                            <TabInfo info={listInfo} />
                        ),
                    },
                    (
                        account?.canGetListCompanyNotifyConfig &&
                        {
                            key: 'notifyConfig',
                            label: 'Cấu hình thông báo',
                            children: (
                                <TabNotifyConfig id={company.id} cpCode={cpCode} templateId={company.templateId} />
                            )
                        }
                    )
                ]}
            />
            <Modal
                title='Thêm lịch sử tương tác'
                open={isOpenModalCreateHistory}
                footer={null}
                onCancel={() => setIsOpenModalCreateHistory(false)}>
                <Divider />
                <Form form={formCreateHistory} onFinish={handleCreateHistory} layout="vertical" autoComplete='off'>
                    <Space>
                        <Form.Item name='cpCode' label='Công ty' initialValue={cpCode}>
                            <Input disabled value={cpCode || ''} />
                        </Form.Item>
                        <Form.Item name='ownerBy' label='Nhân sự' initialValue={account?.username}>
                            <Input disabled value={account?.username} />
                        </Form.Item>
                    </Space>
                    <Form.Item name='type' label='Loại tương tác'
                        rules={[{ required: true, message: 'Vui lòng chọn loại tương tác!' }]}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn loại"
                            options={partnerHistoryOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name='result' label='Nội dung'
                        rules={[{ required: true, message: 'Vui lòng nhập nội dung!' }]}>
                        <Input.TextArea />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Button type='primary' htmlType='submit'>Thêm</Button>
                    </div>
                </Form>
            </Modal>
            <Modal
                title='Cập nhật thông tin liên hệ'
                open={isOpenModalUpdateInfo}
                footer={null}
                onCancel={() => setIsOpenModalUpdateInfo(false)}>
                <Divider />
                <Form form={formUpdateInfo} onFinish={handleUpdateInfo} labelCol={{ span: 5 }} labelAlign='left' autoComplete='off'>
                    <Form.Item name='phone' label='Số điện thoại'>
                        <Input placeholder={info.phone} />
                    </Form.Item>
                    <Form.Item name='email' label='Email'>
                        <Input placeholder={info.email} />
                    </Form.Item>
                    <Form.Item name='director' label='Đại diện'>
                        <Input placeholder={info.director} />
                    </Form.Item>
                    <Form.Item name='address' label='Địa chỉ'>
                        <Input placeholder={info.address} />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Button type='primary' htmlType='submit'>Cập nhật</Button>
                    </div>
                </Form>
            </Modal>
        </div>
    )
}
